import { useState } from "react";
import { motion } from "motion/react";
import {
  LayoutDashboard,
  Server,
  ListChecks,
  ShieldAlert,
} from "lucide-react";
import { MyAssetsTab } from "./dashboard/MyAssetsTab";
import { PoamTodoTab } from "./dashboard/PoamTodoTab";
import { ExceptionsTab } from "./dashboard/ExceptionsTab";

/* ── Tab config ─────────────────────────────────────────────────────────── */

const tabs = [
  { id: "assets", label: "My Assets", icon: Server },
  { id: "poam", label: "POA&M / To-Do", icon: ListChecks },
  { id: "exceptions", label: "Exceptions", icon: ShieldAlert },
] as const;

type TabId = (typeof tabs)[number]["id"];

/* ── Component ──────────────────────────────────────────────────────────── */

export function Dashboard() {
  const [activeTab, setActiveTab] = useState<TabId>("assets");

  return (
    <div className="space-y-6">
      {/* ── Page Header ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <LayoutDashboard className="size-4 text-indigo-500 dark:text-indigo-400" />
            <p className="text-[10px] font-black tracking-[0.12em] text-indigo-500 dark:text-indigo-400">
              Platform Owner
            </p>
          </div>
          <h1>Dashboard</h1>
          <p className="text-xs text-[#64748b] dark:text-white/40 mt-0.5">
            Owned assets, remediation tasks, and risk exceptions at a glance.
          </p>
        </div>
      </div>

      {/* ── Tab Bar ── */}
      <div className="flex items-center gap-1 bg-[#f1f5f9] dark:bg-white/[0.04] rounded-xl p-1 w-fit">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all duration-200 ${
                isActive
                  ? "bg-white dark:bg-white/[0.08] text-[#0f172a] dark:text-white shadow-sm"
                  : "text-[#64748b] dark:text-white/35 hover:text-[#0f172a] dark:hover:text-white/60"
              }`}
            >
              <Icon className="size-3.5" />
              {tab.label}
              {isActive && (
                <motion.div
                  layoutId="active-tab-indicator"
                  className="absolute inset-0 rounded-lg bg-white dark:bg-white/[0.08] shadow-sm -z-10"
                  transition={{ type: "spring", bounce: 0.15, duration: 0.4 }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* ── Tab Content ── */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <div className={activeTab === "assets" ? "block" : "hidden"}>
          <MyAssetsTab />
        </div>
        <div className={activeTab === "poam" ? "block" : "hidden"}>
          <PoamTodoTab />
        </div>
        <div className={activeTab === "exceptions" ? "block" : "hidden"}>
          <ExceptionsTab />
        </div>
      </motion.div>
    </div>
  );
}
